import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookingState } from '../../modules/booking-state/BookingState';
import { ServiceRequest, ServiceType, VehicleServiceHistory } from '../../modules/entities/Entities';
import { ServiceRequestPageContext } from './ServiceRequestPageContext';
import { ServiceRequestPageFormData } from './ServiceRequestPageFormData';
import { createServiceRequestFormSchema } from './ServiceRequestPageFormSchema';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { FormRef } from '../../modules/forms/Form';
import { Wizard, WizardNavigate } from '../../modules/wizard/Wizard';
import { Steps } from '../../modules/wizard/Steps';
import { useGetServiceTypes, useGetVehicleServiceHistory } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { ServiceRequestPageViewProps } from './ServiceRequestPageViewProps';

const defaultFormData: ServiceRequestPageFormData = {
  mileage: 0,
  serviceTypeName: '',
  serviceCommentary: '',
};

function createFormData(request: ServiceRequest): ServiceRequestPageFormData {
  return {
    mileage: request.mileage,
    serviceTypeName: request.serviceType.name,
    serviceCommentary: request.serviceComments,
  };
}

function createServiceRequest(data: ServiceRequestPageFormData, serviceTypes: ServiceType[]): ServiceRequest {
  const selectedServiceType = serviceTypes.find((x) => x.name === data.serviceTypeName);

  return {
    mileage: data.mileage!,
    serviceType: selectedServiceType!,
    serviceComments: data.serviceCommentary,
  };
}

function ContextAccessor({ vehicleDetails, serviceRequest }: BookingState): ServiceRequestPageContext | null {
  if (!vehicleDetails) {
    return null;
  }

  return {
    vehicleDetails,
    serviceRequest,
  };
}

export const ServiceRequestPage = withPageContext(ContextAccessor, ({ pageContext }) => {
  const viewProvider = useViewProvider();
  const [bookingState, setBookingContext] = useBookingContext();
  const formRef = useRef<FormRef<ServiceRequestPageFormData>>(null);
  const [initialFormData] = useState<ServiceRequestPageFormData>(() =>
    pageContext.serviceRequest ? createFormData(pageContext.serviceRequest) : defaultFormData,
  );

  const { data: serviceTypes } = useGetServiceTypes();

  // Defaults to the first service type only when nothing valid is selected. The list is
  // refetched every time the customer comes back to this step, and overwriting the field on
  // each load silently replaced the service they had chosen with the first one.
  useEffect(() => {
    if (!serviceTypes?.length || !formRef.current) {
      return;
    }

    const selectedName = formRef.current.getValues().serviceTypeName;

    if (!serviceTypes.some((x) => x.name === selectedName)) {
      formRef.current.setValue('serviceTypeName', serviceTypes[0].name);
    }
  }, [serviceTypes]);
  const { data: vehicleServiceHistory } = useGetVehicleServiceHistory(pageContext.vehicleDetails.vin, {
    enabled: !pageContext.serviceRequest?.mileage,
    onSuccess: (data: VehicleServiceHistory) => {
      setBookingContext({
        ...bookingState,
        vehicleServiceHistory: data,
      });

      formRef.current!.setValue('mileage', data.mileage);
    },
  });

  // The schema needs the recorded mileage as its lower bound. On a return visit the history is
  // not fetched again, so it comes from the booking state instead — otherwise the form had no
  // schema at all: an empty or lowered mileage passed, and the value stayed a string.
  const recordedMileage = vehicleServiceHistory?.mileage ?? bookingState.vehicleServiceHistory?.mileage;
  const formSchema = useMemo(
    () => (recordedMileage === undefined ? undefined : createServiceRequestFormSchema(recordedMileage)),
    [recordedMileage],
  );

  const isLoading = !serviceTypes || (!vehicleServiceHistory && !pageContext.serviceRequest);

  const onNext: WizardNavigate = (nextStep) => {
    formRef.current!.submit((data) => {
      const serviceRequest = createServiceRequest(data, serviceTypes!);
      const state: BookingState = {
        ...bookingState,
        serviceRequest,
      };

      setBookingContext(state);
      nextStep(state);
    });
  };
  const onPrevious: WizardNavigate = (previousStep) => {
    previousStep(bookingState);
  };

  const viewProps: ServiceRequestPageViewProps = {
    formRef,
    formSchema,
    initialFormData,
    vehicleServiceHistory,
    serviceTypes,
  };

  return (
    <Wizard currentStep={Steps.SelectService} onNext={onNext} onPrevious={onPrevious} disabled={isLoading}>
      {viewProvider.renderServiceRequestPage(viewProps)}
    </Wizard>
  );
});
